import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Lock, CheckCircle, ArrowLeft, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import apiClient from '../../utils/apiClient';

export default function ResetPassword() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const tokenParam = searchParams.get('token') || '';
  const emailParam = searchParams.get('email') || '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Validate params on mount
  useEffect(() => {
    if (!tokenParam || !emailParam) {
      setError(t('auth.invalidResetLink') || 'Invalid or missing reset link. Please request a new one.');
    }
  }, [tokenParam, emailParam, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError(t('auth.passwordMinLength') || 'Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t('auth.passwordsMismatch') || 'Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await apiClient.post('/auth/reset-password', {
        email: emailParam,
        token: tokenParam,
        newPassword: newPassword
      });
      setSuccess(true);
      toast.success(t('auth.passwordResetSuccess') || 'Password reset successfully!');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to reset password';
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <h1 className="font-heading text-2xl font-bold text-gray-900">{t('auth.setNewPassword') || 'Set New Password'}</h1>
          <p className="text-gray-600 mt-2">
            {success
              ? (t('auth.passwordUpdated') || 'Your password has been updated.')
              : (t('auth.enterNewPassword') || 'Enter your new password below.')}
          </p>
        </div>

        {success ? (
          <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4 text-center">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mx-auto">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <p className="text-sm text-gray-700">{t('auth.canNowLogin') || 'You can now log in with your new password.'}</p>
            <Button className="w-full" onClick={() => navigate('/login')}>
              {t('auth.goToLogin') || 'Go to Login'}
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 bg-white p-6 rounded-2xl border border-gray-200 shadow-sm">
            {error && (
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3">
                <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <div>
              <Label htmlFor="new-password">{t('auth.newPassword') || 'New Password'}</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="••••••••"
                required
                minLength={8}
                className="mt-1"
                autoFocus
                disabled={!tokenParam || !emailParam}
              />
              <p className="text-xs text-gray-500 mt-1">{t('auth.passwordMinLength') || 'At least 8 characters'}</p>
            </div>

            <div>
              <Label htmlFor="confirm-password">{t('auth.confirmPassword') || 'Confirm Password'}</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                required
                minLength={8}
                className="mt-1"
                disabled={!tokenParam || !emailParam}
              />
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={loading || !tokenParam || !emailParam || !newPassword || !confirmPassword}
            >
              <Lock className="w-4 h-4 mr-2" />
              {loading ? (t('common.loading') || 'Resetting...') : (t('auth.resetPassword') || 'Reset Password')}
            </Button>
          </form>
        )}

        <div className="text-center">
          <Link to="/login" className="text-sm text-primary hover:text-primary-hover inline-flex items-center gap-1">
            <ArrowLeft className="w-3.5 h-3.5" />
            {t('auth.backToLogin') || 'Back to Login'}
          </Link>
        </div>
      </div>
    </div>
  );
}
